import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SectionNav from '../../../components/layout/SectionNav';
import { researchTabs } from '../../../nav/publicNav';
import { Loader2, AlertTriangle, TrendingUp, TrendingDown, ChevronLeft, ChevronRight, Shield } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import ExportButton from '../../../components/ui/ExportButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/india`;

export default function DebtMonitorPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState('debt_desc');
  const [redFlagsOnly, setRedFlagsOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchData = useCallback(async (pg) => {
    setLoading(true);
    setError(null);
    const p = pg || page;
    try {
      const [listRes, summaryRes] = await Promise.all([
        fetch(`${API}/debt-monitor?sort=${sort}&red_flags_only=${redFlagsOnly}&page=${p}&page_size=30`),
        fetch(`${API}/debt-monitor/summary`),
      ]);
      const [listData, summaryData] = await Promise.all([listRes.json(), summaryRes.json()]);
      setItems(listData.items || []);
      setSummary(summaryData);
      setPage(p);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, [sort, redFlagsOnly, page]);

  const fetchDetail = async (isin) => {
    setDetailLoading(true);
    try {
      const res = await fetch(`${API}/debt-monitor/${isin}/charges`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setDetail(data);
    } catch (e) { setError(e.message); }
    setDetailLoading(false);
  };

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="space-y-4">
      <SectionNav tabs={researchTabs} />
      <div>
        <h1 className="text-xl font-bold flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-500" /> Debt Monitor
        </h1>
        <p className="text-xs text-muted-foreground">Track companies with rising debt, MCA charges, and credit risk indicators</p>
      </div>

      {summary && (
        <div className="grid grid-cols-3 gap-3">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-xs text-muted-foreground">Companies with Debt</p>
              <p className="text-2xl font-bold">{summary.total_with_debt}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-xs text-muted-foreground">Rising Debt YoY</p>
              <p className="text-2xl font-bold text-red-500">{summary.rising_debt_count}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-xs text-muted-foreground">With MCA Charges</p>
              <p className="text-2xl font-bold text-amber-500">{summary.companies_with_charges}</p>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex gap-1">
          {[
            { key: 'debt_desc', label: 'Highest Debt' },
            { key: 'change_desc', label: 'Biggest Change' },
          ].map(s => (
            <Button key={s.key} size="sm" variant={sort === s.key ? 'default' : 'outline'}
              className={`text-xs h-7 ${sort === s.key ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
              onClick={() => { setSort(s.key); setPage(1); }}>
              {s.label}
            </Button>
          ))}
        </div>
        <Button size="sm" variant={redFlagsOnly ? 'default' : 'outline'}
          className={`text-xs h-7 ${redFlagsOnly ? 'bg-red-600 hover:bg-red-700 text-white' : ''}`}
          onClick={() => { setRedFlagsOnly(!redFlagsOnly); setPage(1); }}>
          <AlertTriangle className="w-3 h-3 mr-1" /> Red Flags Only
        </Button>
        <ExportButton rows={items} filename="debt-monitor" className="ml-auto" />
      </div>

      {detail && (
        <Card className="border-amber-500/30">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">Debt History & Charges</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setDetail(null)}>Close</Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {detail.debt_history?.length > 0 && (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={detail.debt_history}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="period" tick={{ fontSize: 9 }} className="fill-muted-foreground" />
                  <YAxis tick={{ fontSize: 9 }} className="fill-muted-foreground" />
                  <Tooltip />
                  <Bar dataKey="debt" name="Debt (Cr)" fill="#ef4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
            {detail.charges?.length > 0 ? (
              <div className="space-y-2">
                <p className="text-xs font-semibold">MCA Charges ({detail.charges.length})</p>
                {detail.charges.map((c, i) => (
                  <div key={i} className="flex items-start gap-3 text-xs border-l-2 border-amber-500 pl-3 py-1">
                    <div>
                      <p className="font-medium">{c.holder || 'Unknown holder'}</p>
                      <p className="text-muted-foreground">
                        Created: {c.created || '—'} · Amount: {c.amount ? `₹${(c.amount / 1e7).toFixed(1)}Cr` : '—'}
                      </p>
                      <Badge variant={c.status === 'Satisfied' ? 'secondary' : 'destructive'} className="text-[9px] mt-0.5">
                        {c.status || 'Open'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">No MCA charges on record.</p>
            )}
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <div className="text-red-500 text-sm p-4 bg-red-50 dark:bg-red-950/20 rounded-lg">{error}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-emerald-800 text-white">
                <th className="text-left py-2 px-3 font-semibold text-[10px] uppercase">Company</th>
                <th className="text-left py-2 px-3 font-semibold text-[10px] uppercase">Sector</th>
                <th className="text-right py-2 px-3 font-semibold text-[10px] uppercase">Debt (Cr)</th>
                <th className="text-right py-2 px-3 font-semibold text-[10px] uppercase">YoY Change</th>
                <th className="text-right py-2 px-3 font-semibold text-[10px] uppercase">Debt/Rev</th>
                <th className="text-center py-2 px-3 font-semibold text-[10px] uppercase">Charges</th>
                <th className="text-center py-2 px-3 font-semibold text-[10px] uppercase">Flag</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r, i) => (
                <tr key={i} className="border-b border-muted/20 hover:bg-muted/20 cursor-pointer"
                  onClick={() => fetchDetail(r.isin)}>
                  <td className="py-2 px-3 font-medium">
                    <button className="hover:text-emerald-600 hover:underline text-left"
                      onClick={(e) => { e.stopPropagation(); r.symbol && navigate(`/public/india-company/${r.symbol}`); }}>
                      {r.company_name}
                    </button>
                  </td>
                  <td className="py-2 px-3 text-muted-foreground">{r.sector || '—'}</td>
                  <td className="py-2 px-3 text-right font-medium">
                    {r.latest_debt != null ? `₹${r.latest_debt.toLocaleString()}` : '—'}
                  </td>
                  <td className="py-2 px-3 text-right">
                    {r.debt_change_pct != null ? (
                      <span className={`font-semibold ${r.debt_change_pct > 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                        {r.debt_change_pct > 0 ? '+' : ''}{r.debt_change_pct}%
                      </span>
                    ) : '—'}
                  </td>
                  <td className="py-2 px-3 text-right text-muted-foreground">{r.debt_to_revenue ?? '—'}x</td>
                  <td className="py-2 px-3 text-center">{r.charge_count || 0}</td>
                  <td className="py-2 px-3 text-center">
                    {r.red_flag && <AlertTriangle className="w-3.5 h-3.5 text-red-500 mx-auto" />}
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={7} className="py-12 text-center text-muted-foreground">No debt data available yet — run fundamentals ingestion first.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
